import { useTranslation } from "react-i18next";

import { Badge, Card } from "~/components/ui";
import { useRecordUpdateTargets } from "~/features/dataImportProjects/hooks/useRecordUpdateTargets";

interface RecordUpdateTargetsPanelProps {
  dataImportProjectId: string;
}

// i18next-extract runs with removeUnusedKeys, so every key must appear as a literal in the source.
// Template-literal keys would be pruned — map the backend enums to literal keys here instead, and
// keep these in sync with the backend's RecordUpdateLifecycle / batch status values.
const LIFECYCLE_LABEL_KEY: Record<string, string> = {
  edit: "feature.recordUpdate.targets.lifecycle.edit",
  deactivate: "feature.recordUpdate.targets.lifecycle.deactivate",
};

// The backend reports a batch as "executed" once every import job of that batch has a successful
// run, "submitted" otherwise.
const STATUS_LABEL_KEY: Record<string, string> = {
  executed: "feature.recordUpdate.targets.status.executed",
  submitted: "feature.recordUpdate.targets.status.submitted",
};

const STATUS_TONE: Record<string, "status-published" | "status-processing"> = {
  executed: "status-published",
  submitted: "status-processing",
};

/**
 * Read-only listing of the target batches registered on a record-update Data Import Project.
 * Renders nothing for a DIP that is not record-update: the query reports a null lifecycle for
 * those, which is the documented UI discriminator, so no separate DIP-kind lookup is needed.
 */
export function RecordUpdateTargetsPanel({ dataImportProjectId }: RecordUpdateTargetsPanelProps) {
  const { t } = useTranslation();
  const { lifecycle, batches, loading } = useRecordUpdateTargets(dataImportProjectId);

  // Hide the panel entirely while the lifecycle is unknown: rendering an empty card on every
  // non-record-update DIP for the duration of the query would be noise on the common path.
  if (loading || lifecycle === null) return null;

  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-element-contextual text-fg-1">{t("feature.recordUpdate.targets.title")}</h2>
        {LIFECYCLE_LABEL_KEY[lifecycle] ? (
          <Badge tone="score-b">{t(LIFECYCLE_LABEL_KEY[lifecycle])}</Badge>
        ) : (
          <Badge tone="score-b">{lifecycle}</Badge>
        )}
      </div>

      {batches.length === 0 ? (
        <p className="text-12-bold text-fg-2">{t("feature.recordUpdate.targets.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {batches.map((batch) => (
            <li
              key={batch.dataSourceId}
              className="flex flex-wrap items-center gap-3 rounded-[var(--radius-md)] border border-border-default bg-bg-surface px-4 py-3"
            >
              <span className="font-mono text-12-bold text-fg-2">{batch.dataSourceId}</span>
              <Badge tone={STATUS_TONE[batch.status] ?? "status-draft"}>
                {STATUS_LABEL_KEY[batch.status] ? t(STATUS_LABEL_KEY[batch.status]) : batch.status}
              </Badge>
              <span className="text-12-bold text-fg-2">
                {t("feature.recordUpdate.targets.targetCount", { count: batch.targets.length })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
